import type { GenericSchema, InferOutput } from 'valibot'
import { parse, safeParse } from 'valibot'

/**
 * Configuration for creating Solr query options for a specific Solr endpoint and response schema.
 * Returns { queryKey, queryFn, enabled? } for use with Vue Query.
 */
interface SolrQueryConfig<
  TParams extends Record<string, any> | undefined = undefined,
  TSchema extends GenericSchema = GenericSchema,
> {
  /**
   * The Solr API endpoint path
   */
  endpoint: MaybeRefOrGetter<string>

  /**
   * Reactive query parameters to pass to the Solr endpoint.
   */
  params?: MaybeRef<TParams>

  /**
   * Valibot schema to use for parsing and validating the Solr response.
   */
  schema: TSchema

  /**
   * Guard function to determine when the query should be enabled.
   */
  enabled?: (params: TParams | undefined) => boolean

  /**
   * Use safeParse instead of parse — returns undefined on validation failure instead of throwing.
   * Use for non-critical queries where a schema mismatch should not crash the page.
   */
  safe?: boolean
}

interface SolrQueryContext {
  fetch: ReturnType<typeof useRequestFetch>
  user: ReturnType<typeof useUserSession>['user']
}

export function useSolrQueryOptions<
  TParams extends Record<string, any> | undefined = undefined,
  TSchema extends GenericSchema = GenericSchema,
>(config: SolrQueryConfig<TParams, TSchema>) {
  // Forwards cookies during SSR so licensed endpoints don't 401
  const fetch = useRequestFetch()

  const { user } = useUserSession()

  return buildSolrQueryOptions(config, { fetch, user })
}

// Takes fetch and user as arguments, because the composables that resolve them need the Nuxt context
export function buildSolrQueryOptions<
  TParams extends Record<string, any> | undefined = undefined,
  TSchema extends GenericSchema = GenericSchema,
>(config: SolrQueryConfig<TParams, TSchema>, { fetch, user }: SolrQueryContext) {
  const { endpoint, params, schema, enabled, safe } = config

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- fetch/schema/safe are stable composable inputs, not real deps
  return {

    queryKey: computed(() => ['solr', user.value?.userId, toValue(endpoint), toValue(params)] as const),
    async queryFn({ signal }: { signal?: AbortSignal }): Promise<InferOutput<TSchema> | undefined> {
      const res = await fetch(`/api/solr/${toValue(endpoint)}`, {
        params: params !== undefined ? toValue(params) : undefined,
        signal,
      })

      if (safe) {
        const result = safeParse(schema, res)
        return result.success ? result.output : undefined
      }

      return parse(schema, res)
    },
    enabled: enabled ? computed(() => enabled(toValue(params))) : undefined,
  }
}
